/**
 * /reports — AI Report Builder hub (Phase 0+1).
 * Shows pinned reports, category sections, and the "Build with AI" entry point.
 */
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useState } from "react";
import {
  reportCatalogQueryOptions,
  type ReportDefinition,
  type CatalogSection,
} from "@/lib/queries";
import { api } from "@/lib/api";
import { ReportIcon } from "@/components/ReportIcon";

export const Route = createFileRoute("/_auth/reports/")({
  component: ReportsHub,
});

const CATEGORY_ICONS: Record<string, string> = {
  construction: "🏗️",
  financial: "📊",
  payroll: "💰",
  tax: "🧾",
  compliance: "✅",
  general: "📋",
  saas: "💎",
  custom: "⭐",
};

const CATEGORY_LABELS: Record<string, string> = {
  construction: "Construction",
  financial: "Financial",
  payroll: "Payroll",
  tax: "Tax",
  compliance: "Compliance",
  general: "General",
  saas: "SaaS Metrics",
  custom: "Custom",
};

function ReportCard({ report }: { report: ReportDefinition }) {
  return (
    <Link
      to="/reports/$reportId"
      params={{ reportId: report.id }}
      className="block p-4 rounded-lg border border-slate-200 dark:border-gray-800 bg-white dark:bg-gray-900 hover:border-blue-400 dark:hover:border-blue-500 hover:shadow-sm transition group"
    >
      <div className="flex items-start gap-3">
        {report.icon && (
          <span className="mt-0.5 flex-shrink-0 text-slate-500 dark:text-slate-400 group-hover:text-blue-500">
            <ReportIcon name={report.icon} fontSize="small" />
          </span>
        )}
        <div className="min-w-0 flex-1">
          <div className="font-medium text-slate-900 dark:text-slate-100 group-hover:text-blue-600 dark:group-hover:text-blue-400 truncate">
            {report.name}
          </div>
          {report.description && (
            <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 leading-snug line-clamp-2">
              {report.description}
            </div>
          )}
          <div className="flex items-center gap-2 mt-2">
            {report.ai_built && (
              <span className="inline-flex items-center gap-1 text-xs bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 px-1.5 py-0.5 rounded">
                ✨ AI built
              </span>
            )}
            {report.visibility === "system" && (
              <span className="text-xs text-slate-400 dark:text-slate-600">System</span>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}

function PinnedSection({ reports }: { reports: ReportDefinition[] }) {
  if (!reports || reports.length === 0) return null;
  return (
    <div className="mb-8">
      <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-3">
        ⭐ Pinned
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {reports.map((r) => (
          <ReportCard key={r.id} report={r} />
        ))}
      </div>
    </div>
  );
}

function CategorySection({ section }: { section: CatalogSection }) {
  const [expanded, setExpanded] = useState(true);
  const icon = CATEGORY_ICONS[section.category] ?? "📋";

  return (
    <div className="mb-6">
      <button
        onClick={() => setExpanded((e) => !e)}
        className="flex items-center gap-2 mb-3 w-full text-left group"
      >
        <span>{icon}</span>
        <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 group-hover:text-slate-700 dark:group-hover:text-slate-300">
          {section.label}
        </h2>
        <span className="text-xs text-slate-400 dark:text-slate-600 ml-1">
          ({(section.reports ?? []).length})
        </span>
        <span className="ml-auto text-slate-400 text-sm">{expanded ? "▾" : "▸"}</span>
      </button>
      {expanded && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {(section.reports ?? []).map((r) => (
            <ReportCard key={r.id} report={r} />
          ))}
        </div>
      )}
    </div>
  );
}

function BuildWithAICard() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
      <Link
        to="/reports/build"
        className="block p-5 rounded-xl border-2 border-dashed border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-950/30 hover:border-blue-500 dark:hover:border-blue-500 hover:bg-blue-100 dark:hover:bg-blue-950/50 transition"
      >
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center flex-shrink-0">
            <span className="text-white text-lg">✨</span>
          </div>
          <div>
            <p className="font-semibold text-blue-900 dark:text-blue-100">Build a report with AI</p>
            <p className="text-sm text-blue-700 dark:text-blue-300 mt-0.5">
              Type what you want to see — get a live table in seconds.
            </p>
          </div>
          <div className="ml-auto text-blue-600 dark:text-blue-400 text-xl flex-shrink-0">→</div>
        </div>
      </Link>

      <Link
        to="/reports/charts"
        className="block p-5 rounded-xl border-2 border-dashed border-purple-300 dark:border-purple-700 bg-purple-50 dark:bg-purple-950/30 hover:border-purple-500 dark:hover:border-purple-500 hover:bg-purple-100 dark:hover:bg-purple-950/50 transition"
      >
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-lg bg-purple-600 flex items-center justify-center flex-shrink-0">
            <span className="text-white text-lg">📊</span>
          </div>
          <div>
            <p className="font-semibold text-purple-900 dark:text-purple-100">Build a chart with AI</p>
            <p className="text-sm text-purple-700 dark:text-purple-300 mt-0.5">
              Describe a chart — export as PNG, JPEG, or Excel.
            </p>
          </div>
          <div className="ml-auto text-purple-600 dark:text-purple-400 text-xl flex-shrink-0">→</div>
        </div>
      </Link>
    </div>
  );
}

function ReportsHub() {
  const { data, isLoading } = useQuery(reportCatalogQueryOptions());

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      <div className="bg-white border-b border-slate-200 px-6 py-4 dark:bg-gray-900 dark:border-gray-800 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Reports</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
              {data?.total != null ? `${data.total} reports` : "Browse, run, or build reports"}
            </p>
          </div>
          <Link
            to="/reports/build"
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition"
          >
            ✨ Build with AI
          </Link>
        </div>
      </div>

      <div className="overflow-y-auto flex-1 px-6 py-6 bg-slate-50 dark:bg-gray-950">
        <div className="max-w-6xl mx-auto">
          <BuildWithAICard />

          {isLoading && (
            <div className="text-sm text-slate-500 dark:text-slate-400 py-8 text-center">
              Loading reports…
            </div>
          )}

          {data && (
            <>
              <PinnedSection reports={data.pinned ?? []} />
              {(data.sections ?? []).map((section) => (
                <CategorySection key={section.category} section={section} />
              ))}
              {data.total === 0 && (
                <div className="text-center py-16 text-slate-500 dark:text-slate-400">
                  <p className="text-4xl mb-3">📊</p>
                  <p className="font-medium">No reports yet</p>
                  <p className="text-sm mt-1">Build your first report with AI or contact support to add system reports.</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
